import { useEffect, useState } from 'react'

/** Below this, the change is a URL bar or a rotation, not a keyboard. */
const KEYBOARD_MIN_PX = 24

/**
 * How much of the screen the software keyboard is covering, in CSS pixels.
 *
 * iOS Safari does not resize the layout viewport when the keyboard opens — it
 * shrinks the *visual* viewport and scrolls it — so `100dvh` still measures the
 * whole screen, `bottom: 0` is still the bottom of the screen, and a bottom
 * sheet stays underneath the keys with its Save button out of reach. The gap
 * between the two viewports is the only honest measure of the overlap.
 *
 * Sampled on an animation frame because the two events fire together and
 * repeatedly through the keyboard's slide, and re-rendering a sheet on each of
 * them is exactly what makes it look like it is fighting the keyboard.
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0)

  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    let frame = 0
    const update = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const overlap = window.innerHeight - viewport.height - viewport.offsetTop
        setInset(overlap > KEYBOARD_MIN_PX ? Math.round(overlap) : 0)
      })
    }

    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    return () => {
      cancelAnimationFrame(frame)
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
    }
  }, [])

  return inset
}
